import { AsyncStatus, StageState, UpdateState } from "enums";
import { IDevice, ISnapshot, ISnapshotColumnDef, ISnapshotRow, IUpdate } from "interfaces";
import { DateTime } from "luxon";
import { DevicesService, FiltersService, IGetAffectedResponse, IGetAffectedResult, LocationsService, ManufacturersService, SnapshotsService, UpdatesService } from "services";
import { assertUnreachable, isObject, Stale } from "services/Utils";
import { ReportsFieldType } from "./enums/ReportsFieldType";
import { TableId } from "./enums/TableId";
import { IField, IRow, ITable, IUpdateParams } from "./ReportsModel";

export interface ILoadResult {
    tables: ITable[],
    modifiedAt: Date,
    rowsByTableId: {
        [tableId: string]: IRow[],
    },
    // For the "snapshot" and "update" modes, we need to load the update first.
    // The reports page needs it to be able to show controls to select a stage or filter.
    update?: IUpdate,
    affected?: IGetAffectedResponse,
    // Sometimes we want to show a custom error message for things we don't support,
    // like if the user has selected "isSnapshot: true" for an individual filter.
    customError?: string,
}

// If adding to fields here, make sure to add the ids to default visible/hidden columns in ReportsModel.tsx
export const allFields: IField[] = [
    {label: 'Manufacturer',      propName: '__manufacturerName', id: 'manufacturerName',  type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Model',             propName: '__modelName',        id: 'modelName',         type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Location',          propName: 'location',           id: 'location',          type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'LocationName',      propName: '__locationName',     id: 'locationName',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'LocationAddress',   propName: '__locationAddress',  id: 'locationAddress',   type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'LocationCity',      propName: '__locationCity',     id: 'locationCity',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'LocationState',     propName: '__locationState',    id: 'locationState',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'LocationType',      propName: '__locationType',     id: 'locationType',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Serial',            propName: 'serialNumber',       id: 'serialNumber',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'ThingID',           propName: 'thingId',            id: 'thingId',           type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Last Seen',         propName: '__lastSeenDt',       id: 'lastSeen',          type: ReportsFieldType.Date,   isGroupable: false, isSortable: true},
    {label: 'Firmware',          propName: '__firmwareVersion',  id: 'firmwareVersion',   type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Menu',              propName: '__menuVersion',      id: 'menuVersion',       type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Mac Address',       propName: 'macAddress',         id: 'macAddress',        type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Update State',      propName: 'deviceState',        id: 'deviceState',       type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'State Detail',      propName: 'deviceStateDetail',  id: 'deviceStateDetail', type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Update ID',         propName: 'updateId',           id: 'updateId',          type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Current Version',   propName: 'currentVersion',     id: 'currentVersion',    type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Component Type',    propName: '__componentType',    id: 'componentType',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Component Key',     propName: '__key',              id: 'componentKey',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
    {label: 'Component Version', propName: '__val',              id: 'componentVersion',  type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
];

const deviceTables: ITable[] = [
    {
        id: TableId.Devices,
        label: 'Devices',
        fields: [
            // We're making these propName's match the snapshot fields so that we can preserve most filters
            // if the user changes from "Targets" to "Results".
            {label: 'Manufacturer',     propName: '__manufacturerName', id: 'manufacturerName', type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Model',            propName: '__modelName',        id: 'modelName',        type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location',         propName: 'location',           id: 'location',         type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Name',    propName: '__locationName',     id: 'locationName',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Address', propName: '__locationAddress',  id: 'locationAddress',  type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location City',    propName: '__locationCity',     id: 'locationCity',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location State',   propName: '__locationState',    id: 'locationState',    type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Type',    propName: '__locationType',     id: 'locationType',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Serial',           propName: 'serialNumber',       id: 'serialNumber',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'ThingID',          propName: 'thingId',            id: 'thingId',          type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Last Seen',        propName: '__lastSeenDt',       id: 'lastSeen',         type: ReportsFieldType.Date,   isGroupable: false, isSortable: true},
            {label: 'Firmware',         propName: '__firmwareVersion',  id: 'firmwareVersion',  type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Menu',             propName: '__menuVersion',      id: 'menuVersion',      type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Mac Address',      propName: 'macAddress',         id: 'macAddress',       type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
        ]
    }, {
        id: TableId.DeviceComponents,
        label: 'Device Components',
        fields: [
            {label: 'Manufacturer',      propName: '__manufacturerName', id: 'manufacturerName', type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Model',             propName: '__modelName',        id: 'modelName',        type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location',          propName: 'location',           id: 'location',         type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Name',     propName: '__locationName',     id: 'locationName',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Address',  propName: '__locationAddress',  id: 'locationAddress',  type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location City',     propName: '__locationCity',     id: 'locationCity',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location State',    propName: '__locationState',    id: 'locationState',    type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Location Type',     propName: '__locationType',     id: 'locationType',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Serial',            propName: 'serialNumber',       id: 'serialNumber',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'ThingID',           propName: 'thingId',            id: 'thingId',          type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Last Seen',         propName: '__lastSeenDt',       id: 'lastSeen',         type: ReportsFieldType.Date,   isGroupable: false, isSortable: true},
            {label: 'Mac Address',       propName: 'macAddress',         id: 'macAddress',       type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Component Type',    propName: '__componentType',    id: 'componentType',    type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Component Key',     propName: '__key',              id: 'componentKey',     type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
            {label: 'Component Version', propName: '__val',              id: 'componentVersion', type: ReportsFieldType.String, isGroupable: true,  isSortable: true},
        ],
    },
]

// Loads all devices in the system and caches them.
export class LoaderAllDevices {
    private loadPromise: Promise<ILoadResult>;
    private loadStatus = AsyncStatus.Initial;

    public load = (timezone: string): Promise<ILoadResult> => {
        // This is a lot of data, so if we've already loaded it once, we'll use the cached promise.
        if (this.loadStatus === AsyncStatus.Loading || this.loadStatus === AsyncStatus.Success) {
            return this.loadPromise;
        }

        this.loadPromise = DevicesService.getAllDevices()
            .then(({ devices, devicesModifiedAt }) => {
                this.loadStatus = AsyncStatus.Success;
                return generateDeviceAndComponentRows(devices, devicesModifiedAt, timezone);
            }, (err) => {
                this.loadStatus = AsyncStatus.Error;
                throw err;
            });

        return this.loadPromise;
    }
}

export interface ILoaderUpdateParams {
    filterId?: number,
    isGlobalFilters: boolean,
    isSnapshot: boolean,
    stageIndex?: number,
    timezone: string
    updateId: number,
}

// Loads the devices for an update and caches them.
// If the user has selected "Results", we load the snapshot instead.
export class LoaderUpdate {
    private getUpdatePromise: Promise<IUpdate>;
    private getUpdateStatus = AsyncStatus.Initial;

    private getAffectedPromise: Promise<{update: IUpdate, affected: IGetAffectedResponse, modifiedAt: Date}>;
    private getAffectedStatus = AsyncStatus.Initial;

    private snapshotPromise: Promise<{
        snapshot?: ISnapshot,
        customError?: string,
    }>;

    private loadStale = new Stale();

    public load = ({ filterId, isGlobalFilters, isSnapshot, stageIndex, updateId }: IUpdateParams, timezone: string): Promise<ILoadResult> => {
        const loadId = this.loadStale.startCall();

        // We'll cache the update and getAffected calls. If the user selects a different stage or filter,
        // then we can still use the original responses.
        if (this.getAffectedStatus === AsyncStatus.Initial || this.getAffectedStatus === AsyncStatus.Error) {
            // Load the update if we haven't already.
            if (this.getUpdateStatus === AsyncStatus.Initial || this.getUpdateStatus === AsyncStatus.Error) {
                this.getUpdatePromise = this.loadUpdate(updateId);
            }

            // Now that we have the update, load the affected devices for all stages and filters.
            this.getAffectedPromise = this.getUpdatePromise.then((update) => {
                return this.loadAffected(update);
            });
        }

        // Go ahead and start loading the snapshot in parallel.
        // If we don't need a snapshot, this will return an empty object.
        // Note: Snapshot caching is implemented in the SnapshotService.
        this.snapshotPromise = this.getUpdatePromise.then((update) => {
            let customError: string;
            let snapshotId: number;

            // Choose which snapshot id to load.
            if (filterId || isGlobalFilters) {
                customError = `Results are only available for an update or a stage.`;
            } else {
                if (stageIndex !== undefined) {
                    const stage = update.rollout.stages[stageIndex];
                    if (stage.state !== StageState.Scheduled) {
                        snapshotId = stage.snapshotId;
                    } else {
                        customError = `This stage hasn't started yet. Results will be available after it starts.`;
                    }
                } else {
                    if (update.state >= UpdateState.Running) {
                        snapshotId = update.snapshotId;
                    } else {
                        customError = `This update hasn't started yet. Results will be available after it starts`;
                    }
                }
            }

            if (snapshotId !== undefined) {
                if (snapshotId === 0) {
                    return {customError: `No results available at this time.`};
                }

                return SnapshotsService.getSnapshot(snapshotId).then((snapshot) => {
                    return {snapshot: snapshot};
                }).catch((err) => {
                    if(err?.response?.status == 404) {
                        return {customError: `No results available at this time.`};
                    }

                    console.error('Error getting snapshot: ', err);
                    throw err;
                });

            } else {
                return {customError: customError};
            }
        });

        // Now that we have all the data we need, generate tables from the returned data.
        return Promise.all([this.getAffectedPromise, this.snapshotPromise]).then(([
            { update, affected, modifiedAt },
            { snapshot, customError },
        ]) => {
            if (this.loadStale.isStale(loadId)) return;

            // Certain configurations aren't supported, like showing a snapshot for a filter.
            // In this case, we'll return an error message.
            let out: ILoadResult = {
                tables: [],
                modifiedAt: new Date(),
                rowsByTableId: {},
            };

            // DataSource: Snapshots
            if (isSnapshot) {
                if (customError) {
                    out.customError = customError;
                } else {
                    out = generateSnapshotRows(snapshot, timezone);
                }
            } 
            // DataSource: Affected devices
            else {
                // If the user has selected a stage or a filter, we want to show the devices
                // for that object instead of for the whole update.
                let affectedResult: IGetAffectedResult;

                if (isGlobalFilters) {
                    // Global filters results
                    if (filterId !== undefined) {
                        affectedResult = affected.rollout.global?.filters?.[filterId];
                    } else {
                        affectedResult = affected.rollout.global.result
                    }
                } else if (stageIndex !== undefined) {
                    // Stage results
                    if (filterId !== undefined) {
                        affectedResult = affected.rollout?.stages?.[stageIndex]?.filters?.[filterId];
                    } else {
                        affectedResult = affected.rollout?.stages?.[stageIndex]?.result;
                    }
                } else {
                    // Whole update results
                    affectedResult = affected.combined;
                }
                out = generateDeviceAndComponentRows(affectedResult?.devices || [], modifiedAt, timezone);
            }

            out.update = update;
            out.affected = affected;
            return out;
        });
    }

    private loadUpdate = (updateId: number): Promise<IUpdate> => {
        this.getUpdateStatus = AsyncStatus.Loading;

        return UpdatesService.getUpdate(updateId)
            .then((update) => {
                this.getUpdateStatus = AsyncStatus.Success;
                return update;
            }, (err) => {
                this.getUpdateStatus = AsyncStatus.Error;
                // Pass error up the promise chain.
                throw err;
            });
    }

    private loadAffected = (update: IUpdate): Promise<{
        update: IUpdate, 
        affected: IGetAffectedResponse,
        modifiedAt: Date,
    }> => {
        this.getAffectedStatus = AsyncStatus.Loading;

        return FiltersService.getAffected(update.manufacturerId, update.deviceModelIds, update.rollout, true)
            .then((affected) => {
                this.getAffectedStatus = AsyncStatus.Success;

                return {
                    // We're passing through the update so that our next promise has access to it.
                    update: update,
                    affected: affected,
                    modifiedAt: new Date(),
                }
            }, (err) => {
                this.getAffectedStatus = AsyncStatus.Error;
                throw err;
            })
    }
}

// We're generating a second "Device Components" table from the devices.
function generateDeviceAndComponentRows(
    devices: IDevice[],
    modifiedAt: Date,
    timezone: string,
): ILoadResult {
    const deviceRows = new Array(devices.length);
    const componentRows = [];
    let curComponentId = 0;

    // We're doing a few transformations to the devices, like looking up manufacturer and model names.
    for (let i = 0; i < devices.length; i++) {
        const d = devices[i];
        const man = ManufacturersService.manufacturersMap[d.manufacturerId];
        const model = ManufacturersService.modelsMap[d.modelId];
        const manName = man?.name || ('Unknown manufacturer: ' + d.manufacturerId);
        const modelName = model?.name || ('Unknown model: ' + d.modelId);
        const lastSeen = DateTime.fromJSDate(d.lastSeen, { zone: timezone });

        const location = LocationsService.locationsMap[d.location];
        const locationName = location?.locationName || 'Unavailable';
        const locationAddress = location?.physicalAddress.address1 || 'Unavailable';
        const locationType = location?.locationTypeCode || 'Unavailable';
        const locationCity = location?.physicalAddress.city || 'Unavailable';
        const locationState = location?.physicalAddress.state || 'Unavailable';

        deviceRows[i] = {
            ...d,
            __lastSeenDt: lastSeen,
            __manufacturerName: manName,
            __modelName: modelName,
            // React can't render objects, so we're converting them to string.
            __firmwareVersion: JSON.stringify(d.firmwareVersion),
            __menuVersion: JSON.stringify(d.menuVersion),
            __rowId: i + '',
            // Display location fields
            __locationName: locationName,
            __locationAddress: locationAddress,
            __locationCity: locationCity,
            __locationState: locationState,
            __locationType: locationType,
        }

        // We're treating each entry in the firmware and menu maps as separate components.
        // This allows us to compute counts of each device on each version of firmware/menu.
        for (const menuOrFirmware of ['Menu', 'Firmware']) {
            const objValue = (menuOrFirmware === 'Menu' ? d.menuVersion : d.firmwareVersion);
            const emptyVal = '<Blank>'

            if (isObject(objValue)) {
                // Some devices have entries that look like {current: '1.2.3', prev: '', status: ''}.
                // For these, we only want "current", and want to ignore "prev" and "status".
                const ignorePrevAndStatus = objValue.hasOwnProperty('current')
                    && objValue.hasOwnProperty('prev')
                    && objValue.hasOwnProperty('status');

                for (const [key, val] of Object.entries(objValue)) {
                    // Skip blank "prev" and "status" fields.
                    if (ignorePrevAndStatus && (key === 'prev' || key === 'status')) {
                        continue;
                    }

                    const rowId = curComponentId + '';
                    curComponentId++;
                    componentRows.push({
                        ...d,
                        __lastSeenDt: lastSeen,
                        __manufacturerName: manName,
                        __modelName: modelName,
                        __componentType: menuOrFirmware,
                        __key: key + '',
                        __val: (val + '') || emptyVal,
                        __rowId: rowId,
                        // Display location fields
                        __locationName: locationName,
                        __locationAddress: locationAddress,
                        __locationType: locationType,
                    });
                }
            }
        }
    }

    return {
        tables: deviceTables,
        modifiedAt: modifiedAt,
        rowsByTableId: {
            [TableId.Devices]: deviceRows,
            [TableId.DeviceComponents]: componentRows,
        },
    }
}

function generateSnapshotRows(
    snapshot: ISnapshot,
    timezone: string,
): ILoadResult {

    const manufacturerIdProp = 'manufacturerId';
    const modelIdProp = 'modelId';
    const manufacturerNameProp = 'manufacturerName';
    const modelNameProp = 'modelName';

    // Convert snapshot columns into our supported fields.
    const fields: IField[] = [];
    for (let i = 0; i < snapshot.metadata.columns.length; i++) {
        const col = snapshot.metadata.columns[i];

        let newType: ReportsFieldType;
        switch (col.dataType) {
            case 'boolean':
            case 'string':
            case 'map':
            case 'list':
                newType = ReportsFieldType.String;
                break;
            case 'number':
                newType = ReportsFieldType.Number;
                break;
            case 'date':
                newType = ReportsFieldType.Date;
                break;
            default:
                assertUnreachable(col.dataType);
        }

        fields.push({
            id: col.id,
            isGroupable: true,
            isSortable: true,
            label: SnapshotsService.getColumnLabel(col.id),
            propName: col.id,
            type: newType,
        })
    }

    // If we have manufacturer or model id's, we're going to add in the names for the user's convenience.
    const manufacturerIdIndex = fields.findIndex(f => f.id === manufacturerIdProp);
    if (manufacturerIdIndex !== -1) {
        // Replace the manufacturerId
        fields.splice(manufacturerIdIndex, 1, {
            id: manufacturerNameProp,
            isGroupable: true,
            isSortable: true,
            label: 'Manufacturer',
            propName: manufacturerNameProp,
            type: ReportsFieldType.String,
        })
    }

    const modelIdIndex = fields.findIndex(f => f.id === modelIdProp);
    if (modelIdIndex !== -1) {
        // Replace the modelId
        fields.splice(modelIdIndex, 1, {
            id: modelNameProp,
            isGroupable: true,
            isSortable: true,
            label: 'Model',
            propName: modelNameProp,
            type: ReportsFieldType.String,
        })
    }

    const tables: ITable[] = [{
        fields: fields,
        id: TableId.Devices,
        label: 'Results',
    }];

    // Precompute which fields that need special handling for performance.
    const boolCols = snapshot.metadata.columns.filter(c => c.dataType === 'boolean')
    const dateCols = snapshot.metadata.columns.filter(c => c.dataType === 'date')
    const objectCols = snapshot.metadata.columns.filter(c => c.dataType === 'list' || c.dataType === 'map')

    // Convert the rows into a format we can render.
    const rows = new Array(snapshot.data.length);

    for (let i = 0; i < snapshot.data.length; i++) {
        const snapRow = snapshot.data[i];
        const row = {
            ...snapRow,
            __rowId: i + '',
        };

        // Convert bools to string
        for (const col of boolCols) {
            row[col.id] = row[col.id] + '';
        }

        // Create new luxon dates
        for (const col of dateCols) {
            row[col.id] = DateTime.fromISO(row[col.id], { zone: timezone })
        }

        // React can't render objects, we need to convert to json
        for (const col of objectCols) {
            row[col.id] = JSON.stringify(row[col.id])
        }

        // Lookup manufacturer and model names
        if (manufacturerIdIndex !== -1) {
            const manId = row[manufacturerIdProp];
            row[manufacturerNameProp] = ManufacturersService.manufacturersMap[manId]?.name || manId;
        }

        if (modelIdIndex !== -1) {
            const modelId = row[modelIdProp];
            row[modelNameProp] = ManufacturersService.modelsMap[modelId]?.name || modelId;
        }

        rows[i] = row;
    }

    return {
        tables: tables,
        modifiedAt: snapshot.metadata.dataGenerationDate,
        rowsByTableId: {
            [TableId.Devices]: rows,
        },
    }
}